import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const JenniferGenesis = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.35, 0.7, 1],
    ['grayscale(100%) brightness(0.35)', 'grayscale(60%) brightness(0.6)', 'grayscale(20%) brightness(0.85)', 'grayscale(0%) brightness(1)']
  );

  return (
    <section ref={ref} className="relative bg-darker border-t border-white/5 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
        {/* Image */}
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden order-2 lg:order-1">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/0a8dbd1ab6196d088ee699a1d0e6492e.png"
            alt="Jennifer Gräser"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-darker lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-10 left-6 right-12 p-6 z-10"
            style={{
              background: 'rgba(5,5,5,0.75)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(114,47,55,0.3)',
            }}
          >
            <p className="text-base font-serif italic text-cream/80 leading-relaxed">
              "Ich kenne diese Dunkelheit. Ich habe auf den gleichen kalten Fliesen gesessen und geweint.{' '}
              <span className="text-gold">Und ich kenne den Weg hinaus in das goldene Licht."</span>
            </p>
          </motion.div>
        </div>

        {/* Text */}
        <div className="relative z-10 flex flex-col justify-center px-8 md:px-14 py-24 space-y-8 order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-wine-light tracking-[0.3em] uppercase text-xs font-semibold"
          >
            Jennifers Geschichte
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight"
          >
            Dies ist kein theoretisches Coaching.{' '}
            <span className="italic text-wine-light block mt-2">
              Ich bin diesen Weg gegangen.
            </span>
          </motion.h2>
          {[
            { delay: 0.2, text: 'Es gab eine Zeit, in der ich auf dem Badezimmerboden saß, das Telefon in der Hand, und nicht wusste, wie ich die nächste Stunde überstehen sollte. Ich war in einer Verbindung gefangen, die sich anfühlte wie Heimat und Hölle gleichzeitig. Ich liebte einen Mann, der mich liebte – aber nicht genug, um sich vollständig zu entscheiden. Und ich war nicht in der Lage loszulassen, weil Loslassen sich anfühlte wie das Ende von mir.' },
            { delay: 0.4, highlight: true, text: 'Das war der dunkelste Moment. Und gleichzeitig: der Beginn meiner Wiedergeburt.' },
            { delay: 0.6, text: 'In den Jahren danach habe ich jeden Stein umgedreht. Ich habe verstanden, warum ich diese Verbindung brauchte – nicht wegen ihm, sondern wegen mir. Wegen der kleinen Jennifer, die gelernt hatte, dass Liebe bedeutet, sich anzupassen, zu warten, sich zu verdienen. Diese Erkenntnis hat mich befreit. Heute begleite ich Frauen auf diesem Weg – nicht als jemand, der über ihnen steht, sondern als jemand, der jeden einzelnen Schritt kennt.' },
            { delay: 0.8, gold: true, text: 'Aus dem tiefsten Schmerz zur absoluten Souveränität. Das ist der Weg. Und ich zeige ihn dir.' },
          ].map((block, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${
                block.gold ? 'text-gold font-serif italic text-xl' : block.highlight ? 'text-cream font-serif italic text-xl' : 'text-cream/60'
              }`}
            >
              {block.text}
            </motion.p>
          ))}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1 }} className="pt-6 border-t border-white/10">
            <p className="text-cream font-semibold tracking-widest uppercase text-sm">Jennifer Gräser</p>
            <p className="text-gold text-xs uppercase tracking-widest mt-1">Gründerin · Coaching Female</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JenniferGenesis;